import { useState, useCallback, useRef } from 'react';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Find a lyric / karaoke / performance video for the current song.
 *
 * The browser NEVER talks to YouTube: it calls `searchLessonVideos`, which
 * holds the API key server-side and owns the 7-day cache. See
 * functions/youtubeSearch.js for why (a CRA env var is public in the bundle).
 *
 * INTENT IS THE INTERESTING PART. A teacher hunting a karaoke track and one
 * hunting a lyric video want different videos for the same song, so intent is
 * a first-class input rather than something to bury in the query box — and it
 * is part of the server's cache key, so the two answers never collide.
 */
export const VIDEO_INTENTS = [
	{ key: 'lyrics', label: 'Lyrics' },
	{ key: 'karaoke', label: 'Karaoke' },
	{ key: 'performance', label: 'Performance' },
];

const useYoutubeSearch = () => {
	const [isSearching, setIsSearching] = useState(false);
	const [results, setResults] = useState([]);
	const [error, setError] = useState('');
	const [searched, setSearched] = useState(false);

	// A slow search must not overwrite a newer one's results. Cheap guard, and
	// the alternative (aborting) buys nothing — the quota is already spent
	// server-side by the time the response is in flight.
	const requestRef = useRef(0);

	const search = useCallback(async (query, intent) => {
		const q = String(query || '').trim();
		if (q.length < 3) return;

		const ticket = ++requestRef.current;
		setIsSearching(true);
		setError('');
		try {
			const callable = httpsCallable(functions, 'searchLessonVideos');
			const res = await callable({ query: q, intent });
			if (ticket !== requestRef.current) return;
			setResults(res.data?.results || []);
			setSearched(true);
		} catch (err) {
			if (ticket !== requestRef.current) return;
			console.error('Video search failed', err);
			// The callable's own messages are written for a teacher mid-lesson
			// (quota exhaustion in particular), so prefer them over a generic one.
			setError(err?.message || 'Video search is unavailable. Paste a link instead.');
			setResults([]);
			setSearched(true);
		} finally {
			if (ticket === requestRef.current) setIsSearching(false);
		}
	}, []);

	const reset = useCallback(() => {
		requestRef.current += 1;
		setResults([]);
		setError('');
		setSearched(false);
		setIsSearching(false);
	}, []);

	return { isSearching, results, error, searched, search, reset };
};

export default useYoutubeSearch;
